/**
 * Paredes da cena (`docs/spec/scene-walls.md`): linhas de segmentos em células, guardadas no
 * container `walls` do documento da cena. Todos leem (a visão é calculada no navegador de cada
 * pessoa); só o mestre cria, muda e apaga.
 *
 * Cada filho `wall`:
 * - `points` ("x,y x,y…", em células, com centésimos) e `closed` (fecha no primeiro ponto);
 * - `sight` normal | limited | none, `move` (bloqueia o movimento), `dir` both | left | right
 *   (esquerda e direita no sentido do primeiro ponto para o segundo, com o y para baixo);
 * - porta: `door: 'door'`, `open` e `locked`. A porta secreta não tem `door`: tem um filho `secret`
 *   que o grupo "todos" não lê, então para o jogador ela é uma parede comum até abrir.
 */
import { NDB_GROUPS } from '../shared.js';
import type { AttrValue, NodeTree, Op, TreeNode } from '../ndb.js';

export const WALLS = 'walls';
export const SECRET = 'secret';

export type WallSight = 'normal' | 'limited' | 'none';
export type WallDir = 'both' | 'left' | 'right';
export type DoorKind = 'none' | 'door' | 'secret';
export type WallProps = { sight: WallSight; move: boolean; dir: WallDir; door: DoorKind };
export type Wall = WallProps & {
  id: string;
  /** pares x, y em células */
  points: number[];
  closed: boolean;
  open: boolean;
  locked: boolean;
  /** filho `secret` (só o mestre recebe) */
  secretId: string | null;
};

export const WALL_DEFAULTS: WallProps = { sight: 'normal', move: true, dir: 'both', door: 'none' };

/** Os tipos da barra: cada um preenche as propriedades. */
export type WallPreset = 'wall' | 'window' | 'ethereal' | 'terrain' | 'door' | 'secret';
export const WALL_PRESETS: Record<WallPreset, WallProps> = {
  wall: WALL_DEFAULTS,
  window: { sight: 'none', move: true, dir: 'both', door: 'none' },
  ethereal: { sight: 'normal', move: false, dir: 'both', door: 'none' },
  terrain: { sight: 'limited', move: true, dir: 'both', door: 'none' },
  door: { sight: 'normal', move: true, dir: 'both', door: 'door' },
  secret: { sight: 'normal', move: true, dir: 'both', door: 'secret' },
};
export const PRESET_ORDER: readonly WallPreset[] = ['wall', 'window', 'ethereal', 'terrain', 'door', 'secret'];

/** O tipo que corresponde às propriedades (null: combinação feita à mão). */
export function presetOf(p: WallProps): WallPreset | null {
  for (const key of PRESET_ORDER) {
    const q = WALL_PRESETS[key];
    if (q.sight === p.sight && q.move === p.move && q.dir === p.dir && q.door === p.door) return key;
  }
  return null;
}

const SIGHTS: readonly WallSight[] = ['normal', 'limited', 'none'];
const DIRS: readonly WallDir[] = ['both', 'left', 'right'];
const r2 = (v: number) => Math.round(v * 100) / 100;

const bool = (v: AttrValue | undefined, fallback: boolean): boolean => (typeof v === 'boolean' ? v : v === 'true' ? true : v === 'false' ? false : fallback);

export function parsePoints(value: AttrValue | undefined): number[] {
  if (typeof value !== 'string') return [];
  const nums = value
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number);
  if (nums.some((n) => !Number.isFinite(n))) return [];
  return nums.length % 2 ? nums.slice(0, -1) : nums;
}

export const formatPoints = (points: number[]): string => {
  const out: string[] = [];
  for (let i = 0; i + 1 < points.length; i += 2) out.push(`${r2(points[i]!)},${r2(points[i + 1]!)}`);
  return out.join(' ');
};

function readWall(node: TreeNode): Wall | null {
  const a = node.attrs;
  const points = parsePoints(a.get('points'));
  if (points.length < 4) return null;
  const secret = node.children.find((c) => c.name === SECRET) ?? null;
  const sight = a.get('sight');
  const dir = a.get('dir');
  const door: DoorKind = a.get('door') === 'door' ? 'door' : secret ? 'secret' : 'none';
  return {
    id: node.id,
    points,
    closed: bool(a.get('closed'), false) && points.length >= 6,
    sight: SIGHTS.includes(sight as WallSight) ? (sight as WallSight) : 'normal',
    move: bool(a.get('move'), true),
    dir: DIRS.includes(dir as WallDir) ? (dir as WallDir) : 'both',
    door,
    open: bool(a.get('open'), false),
    locked: bool(a.get('locked'), false),
    secretId: secret?.id ?? null,
  };
}

export function readWalls(tree: NodeTree): { wallsId: string | null; walls: Wall[] } {
  const container = tree.findChild(tree.root.id, WALLS);
  const walls: Wall[] = [];
  for (const child of container?.children ?? []) {
    if (child.name !== 'wall') continue;
    const wall = readWall(child);
    if (wall) walls.push(wall);
  }
  return { wallsId: container?.id ?? null, walls };
}

/* ------------------------------------------------------------------ operações */

/** Container das paredes (o mestre cria na primeira parede; ninguém mais escreve nele). */
export const wallsContainerOps = (id: string, rootId: string): Op[] => [{ t: 'add', parent: rootId, id, name: WALLS }];

const setAttrs = (node: string, attrs: Record<string, AttrValue | null>): Op[] => Object.entries(attrs).map(([name, value]) => ({ t: 'set', node, name, value }));

/** Atributos das propriedades, só o que difere do padrão (porta secreta vai no filho). */
const propAttrs = (p: WallProps): Record<string, AttrValue | null> => ({
  sight: p.sight === 'normal' ? null : p.sight,
  move: p.move ? null : false,
  dir: p.dir === 'both' ? null : p.dir,
  door: p.door === 'door' ? 'door' : null,
});

/** O filho que esconde a porta secreta de quem não é mestre. */
const secretOps = (wallId: string, id: string): Op[] => [
  { t: 'add', parent: wallId, id, name: SECRET },
  { t: 'perm', node: id, kind: 'group', id: NDB_GROUPS.everyone, permission: 'read', allowance: 'deny' },
];

/**
 * Parede nova. Porta (comum ou secreta) tem exatamente 2 pontos; com mais, vira parede comum.
 * `secretId` é o id do filho `secret`, usado só na porta secreta.
 */
export function addWallOps(containerId: string, id: string, points: number[], closed: boolean, props: WallProps, secretId?: string): Op[] {
  const door = points.length === 4 ? props.door : 'none';
  const attrs: Record<string, AttrValue | null> = { points: formatPoints(points), ...propAttrs({ ...props, door }) };
  if (closed && points.length >= 6) attrs.closed = true;
  const ops: Op[] = [{ t: 'add', parent: containerId, id, name: 'wall' }, ...setAttrs(id, attrs).filter((op) => op.t !== 'set' || op.value !== null)];
  if (door === 'secret' && secretId) ops.push(...secretOps(id, secretId));
  return ops;
}

/** Muda as propriedades. Deixar de ser porta tira `open` e `locked`. */
export function setWallPropsOps(wall: Wall, props: WallProps, secretId: string): Op[] {
  const door = wall.points.length === 4 ? props.door : 'none';
  const ops = setAttrs(wall.id, propAttrs({ ...props, door }));
  if (door === 'none') ops.push(...setAttrs(wall.id, { open: null, locked: null }));
  if (door === 'secret' && !wall.secretId) ops.push(...secretOps(wall.id, secretId));
  if (door !== 'secret' && wall.secretId) ops.push({ t: 'del', node: wall.secretId });
  return ops;
}

export const setPointsOps = (wall: Wall, points: number[]): Op[] => setAttrs(wall.id, { points: formatPoints(points) });

/** Abre, fecha, tranca ou destranca (aberta não fica trancada). */
export function doorStateOps(wall: Wall, state: { open?: boolean; locked?: boolean }): Op[] {
  const attrs: Record<string, AttrValue | null> = {};
  if (state.open !== undefined) attrs.open = state.open ? true : null;
  if (state.locked !== undefined) attrs.locked = state.locked ? true : null;
  if (state.open) attrs.locked = null;
  if (state.locked) attrs.open = null;
  return setAttrs(wall.id, attrs);
}

export const deleteWallsOps = (ids: Iterable<string>): Op[] => [...ids].map((node): Op => ({ t: 'del', node }));

/* ------------------------------------------------------------------ geometria */

export type Segment = [number, number, number, number];

/** Os segmentos da parede (com o que fecha, se for fechada). */
export function segmentsOf(wall: Pick<Wall, 'points' | 'closed'>): Segment[] {
  const p = wall.points;
  const out: Segment[] = [];
  for (let i = 0; i + 3 < p.length; i += 2) out.push([p[i]!, p[i + 1]!, p[i + 2]!, p[i + 3]!]);
  if (wall.closed && p.length >= 6) out.push([p[p.length - 2]!, p[p.length - 1]!, p[0]!, p[1]!]);
  return out;
}

/** Meio da porta (onde fica o ícone). */
export const doorCenter = (wall: Wall): { x: number; y: number } => ({ x: (wall.points[0]! + wall.points[2]!) / 2, y: (wall.points[1]! + wall.points[3]!) / 2 });

/** Encaixa nos cantos e nos meios das células. */
export const snapHalf = (v: number): number => Math.round(v * 2) / 2;

export function rectPoints(ax: number, ay: number, bx: number, by: number): number[] {
  const [x1, x2] = [Math.min(ax, bx), Math.max(ax, bx)];
  const [y1, y2] = [Math.min(ay, by), Math.max(ay, by)];
  return [x1, y1, x2, y1, x2, y2, x1, y2];
}

/** Elipse inscrita no retângulo, como polígono de `n` lados. */
export function ellipsePoints(ax: number, ay: number, bx: number, by: number, n = 24): number[] {
  const cx = (ax + bx) / 2;
  const cy = (ay + by) / 2;
  const rx = Math.abs(bx - ax) / 2;
  const ry = Math.abs(by - ay) / 2;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    out.push(r2(cx + Math.cos(t) * rx), r2(cy + Math.sin(t) * ry));
  }
  return out;
}

export function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** Traço à mão simplificado (Ramer–Douglas–Peucker), com tolerância em células. */
export function simplifyPath(points: number[], tolerance: number): number[] {
  const n = points.length / 2;
  if (n <= 2) return points.slice();
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  while (stack.length) {
    const [i, j] = stack.pop()!;
    let worst = -1;
    let far = 0;
    for (let k = i + 1; k < j; k++) {
      const d = distToSegment(points[k * 2]!, points[k * 2 + 1]!, points[i * 2]!, points[i * 2 + 1]!, points[j * 2]!, points[j * 2 + 1]!);
      if (d > far) {
        far = d;
        worst = k;
      }
    }
    if (worst >= 0 && far > tolerance) {
      keep[worst] = 1;
      stack.push([i, worst], [worst, j]);
    }
  }
  const out: number[] = [];
  for (let k = 0; k < n; k++) if (keep[k]) out.push(points[k * 2]!, points[k * 2 + 1]!);
  return out;
}

/** A parede mais perto do ponto, até `tolerance` células. */
export function hitWall(walls: Wall[], x: number, y: number, tolerance: number): Wall | null {
  let best: Wall | null = null;
  let bestD = tolerance;
  for (const wall of walls) {
    for (const [ax, ay, bx, by] of segmentsOf(wall)) {
      const d = distToSegment(x, y, ax, ay, bx, by);
      if (d <= bestD) {
        bestD = d;
        best = wall;
      }
    }
  }
  return best;
}

/** O vértice mais perto do ponto, até `tolerance` células (só nas paredes dadas). */
export function hitVertex(walls: Wall[], x: number, y: number, tolerance: number): { wall: Wall; index: number } | null {
  let best: { wall: Wall; index: number } | null = null;
  let bestD = tolerance;
  for (const wall of walls) {
    for (let i = 0; i + 1 < wall.points.length; i += 2) {
      const d = Math.hypot(wall.points[i]! - x, wall.points[i + 1]! - y);
      if (d <= bestD) {
        bestD = d;
        best = { wall, index: i / 2 };
      }
    }
  }
  return best;
}

export type Rect = { x: number; y: number; w: number; h: number };

/** Trecho [t0, t1] do segmento dentro do retângulo (Liang–Barsky), ou null. */
function clipToRect(ax: number, ay: number, bx: number, by: number, r: Rect): [number, number] | null {
  const dx = bx - ax;
  const dy = by - ay;
  let t0 = 0;
  let t1 = 1;
  const edges: [number, number][] = [
    [-dx, ax - r.x],
    [dx, r.x + r.w - ax],
    [-dy, ay - r.y],
    [dy, r.y + r.h - ay],
  ];
  for (const [p, q] of edges) {
    if (p === 0) {
      if (q < 0) return null;
      continue;
    }
    const t = q / p;
    if (p < 0) {
      if (t > t1) return null;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return null;
      if (t < t1) t1 = t;
    }
  }
  return t1 - t0 > 1e-9 ? [t0, t1] : null;
}

/** Paredes com algum trecho dentro do retângulo (seleção por caixa). */
export const wallsInRect = (walls: Wall[], r: Rect): Wall[] => walls.filter((w) => segmentsOf(w).some(([ax, ay, bx, by]) => clipToRect(ax, ay, bx, by, r)));

/**
 * Corta as paredes dentro do retângulo ("Remover com retângulo"): o que sobra de cada parede
 * vira paredes novas, com as mesmas propriedades. Porta tocada pelo retângulo some inteira.
 */
export function cutWallsOps(walls: Wall[], r: Rect, containerId: string, newId: () => string): Op[] {
  const ops: Op[] = [];
  for (const wall of walls) {
    const segs = segmentsOf(wall);
    const clips = segs.map(([ax, ay, bx, by]) => clipToRect(ax, ay, bx, by, r));
    if (!clips.some(Boolean)) continue;
    ops.push({ t: 'del', node: wall.id });
    if (wall.door !== 'none') continue;
    // os trechos de fora, na ordem; um trecho que começa onde o anterior terminou continua a peça
    const pieces: number[][] = [];
    let current: number[] = [];
    let continues = false;
    let firstAtStart = false;
    let lastAtEnd = false;
    segs.forEach(([ax, ay, bx, by], i) => {
      const at = (t: number) => [r2(ax + (bx - ax) * t), r2(ay + (by - ay) * t)];
      const clip = clips[i];
      const outside: [number, number][] = !clip ? [[0, 1]] : [...(clip[0] > 1e-9 ? [[0, clip[0]] as [number, number]] : []), ...(clip[1] < 1 - 1e-9 ? [[clip[1], 1] as [number, number]] : [])];
      for (const [u, v] of outside) {
        if (i === 0 && u === 0) firstAtStart = true;
        if (u === 0 && continues) current.push(...at(v));
        else {
          if (current.length >= 4) pieces.push(current);
          current = [...at(u), ...at(v)];
        }
        continues = v === 1;
        lastAtEnd = i === segs.length - 1 && v === 1;
      }
      if (!outside.length || outside[outside.length - 1]![1] !== 1) continues = false;
    });
    if (current.length >= 4) pieces.push(current);
    // parede fechada: a última peça termina no primeiro ponto, onde a primeira começa
    if (wall.closed && firstAtStart && lastAtEnd && pieces.length >= 2) {
      const last = pieces.pop()!;
      pieces[0] = [...last, ...pieces[0]!.slice(2)];
    }
    for (const piece of pieces) {
      const length = segmentsOf({ points: piece, closed: false }).reduce((s, [ax, ay, bx, by]) => s + Math.hypot(bx - ax, by - ay), 0);
      if (length < 0.01) continue;
      ops.push(...addWallOps(containerId, newId(), piece, false, wall));
    }
  }
  return ops;
}

/* ------------------------------------------------------------------ emendas (BRAZ-19) */

/**
 * Tolerância das emendas, em células: duas pontas de parede a menos de um quarto de célula contam como
 * o mesmo ponto.
 * - Ao desenhar ou arrastar um ponto, ele encaixa no vértice da outra parede (`snapToWallVertex`).
 * - A visão e o movimento não passam pela fresta entre as pontas (`wallJoints`, em vision.ts).
 * - "Unir pontas próximas" junta as pontas de vez no documento (`joinWallEndsOps`).
 */
export const WALL_JOIN = 0.25;

/** O ponto do segmento mais perto de (px, py), e a distância até ele. */
function closestOnSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): { x: number; y: number; d: number } {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
  const x = ax + t * dx;
  const y = ay + t * dy;
  return { x, y, d: Math.hypot(px - x, py - y) };
}

/** As pontas de uma parede aberta: o primeiro e o último ponto (com o índice do vértice). A fechada não tem. */
export function wallEnds(wall: Pick<Wall, 'points' | 'closed'>): { index: number; x: number; y: number }[] {
  if (wall.closed) return [];
  const last = wall.points.length / 2 - 1;
  return [0, last].map((index) => ({ index, x: wall.points[index * 2]!, y: wall.points[index * 2 + 1]! }));
}

/**
 * Onde encaixar um ponto desenhado ou arrastado: o vértice de outra parede mais perto de (x, y), até
 * `tol` células. Os vértices da parede `exceptId` (a que está sendo mexida) não contam.
 */
export function snapToWallVertex(walls: Wall[], x: number, y: number, tol: number, exceptId?: string): { x: number; y: number } | null {
  const hit = hitVertex(exceptId ? walls.filter((w) => w.id !== exceptId) : walls, x, y, tol);
  return hit ? { x: hit.wall.points[hit.index * 2]!, y: hit.wall.points[hit.index * 2 + 1]! } : null;
}

/** O ponto mais perto da ponta (x, y) em outra parede (qualquer ponto dela), ou na outra ponta da própria. */
function nearestForEnd(walls: Wall[], points: Map<string, number[]>, wall: Wall, end: { index: number; x: number; y: number }, tol: number): { x: number; y: number; d: number; to: Wall } | null {
  let best: { x: number; y: number; d: number; to: Wall } | null = null;
  for (const other of walls) {
    if (other.id === wall.id) continue;
    for (const [ax, ay, bx, by] of segmentsOf({ points: points.get(other.id) ?? other.points, closed: other.closed })) {
      // fora da caixa do segmento, com a folga: nem calcula
      if (end.x < Math.min(ax, bx) - tol || end.x > Math.max(ax, bx) + tol || end.y < Math.min(ay, by) - tol || end.y > Math.max(ay, by) + tol) continue;
      const c = closestOnSegment(end.x, end.y, ax, ay, bx, by);
      if (c.d <= tol && (!best || c.d < best.d)) best = { ...c, to: other };
    }
  }
  // a forma quase fechada: a outra ponta da própria parede
  const own = points.get(wall.id) ?? wall.points;
  if (own.length >= 6) {
    const index = end.index === 0 ? own.length / 2 - 1 : 0;
    const x = own[index * 2]!;
    const y = own[index * 2 + 1]!;
    const d = Math.hypot(x - end.x, y - end.y);
    if (d <= tol && (!best || d < best.d)) best = { x, y, d, to: wall };
  }
  return best;
}

/** Uma emenda: o trecho da ponta de `from` até o ponto mais perto em `to` (outra parede, ou a própria). */
export type WallJoint = { from: Wall; to: Wall; ax: number; ay: number; bx: number; by: number };

/**
 * As emendas das frestas: cada ponta de parede aberta a até `tol` de outra parede (de qualquer ponto
 * dela, não só das pontas: o T também), ou da outra ponta da própria parede, ganha o trecho que falta
 * até lá. Pontas que já se tocam não precisam, e duas pontas uma perto da outra dão uma emenda só.
 */
export function wallJoints(walls: Wall[], tol = WALL_JOIN): WallJoint[] {
  const out: WallJoint[] = [];
  const seen = new Set<string>();
  const none = new Map<string, number[]>();
  for (const wall of walls) {
    for (const end of wallEnds(wall)) {
      const near = nearestForEnd(walls, none, wall, end, tol);
      if (!near || near.d < 1e-6) continue;
      const a = `${r2(end.x)},${r2(end.y)}`;
      const b = `${r2(near.x)},${r2(near.y)}`;
      const key = a < b ? `${a}|${b}` : `${b}|${a}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ from: wall, to: near.to, ax: end.x, ay: end.y, bx: near.x, by: near.y });
    }
  }
  return out;
}

/**
 * "Unir pontas próximas": cada ponta das paredes `ids` a até `tol` de outra parede (ou da outra ponta
 * da própria) vai para o ponto mais perto dela, e a fresta some de vez. Uma ponta já movida conta no
 * lugar novo, então duas pontas próximas se encontram num ponto só. Devolve as operações e quantas
 * pontas foram unidas.
 */
export function joinWallEndsOps(walls: Wall[], ids: ReadonlySet<string>, tol = WALL_JOIN): { ops: Op[]; joined: number } {
  const points = new Map<string, number[]>();
  let joined = 0;
  for (const wall of walls) {
    if (!ids.has(wall.id) || wall.door !== 'none') continue;
    for (const end of wallEnds(wall)) {
      const current = points.get(wall.id) ?? wall.points;
      const at = { index: end.index, x: current[end.index * 2]!, y: current[end.index * 2 + 1]! };
      const near = nearestForEnd(walls, points, wall, at, tol);
      if (!near || near.d < 1e-6) continue;
      const next = current.slice();
      next[end.index * 2] = r2(near.x);
      next[end.index * 2 + 1] = r2(near.y);
      points.set(wall.id, next);
      joined += 1;
    }
  }
  const ops = walls.filter((w) => points.has(w.id)).flatMap((w) => setPointsOps(w, points.get(w.id)!));
  return { ops, joined };
}
